import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
} from "@mui/material";
import axios from "../axios";
import { useState } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";

const statusColors = {
  PRESENT: "#4caf50",
  ABSENT: "#f44336",
  LATE: "#ff9800",
};

const statusLabels = {
  PRESENT: "P",
  ABSENT: "A",
  LATE: "L",
};

const AttendanceReport = () => {
  const [classID, setClassID] = useState("");
  const [from, setFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));

  const { data: classes } = useQuery("classes", () =>
    axios.get("/classes").then((res) => res.data)
  );

  const {
    data: report,
    isLoading,
    refetch,
  } = useQuery(
    ["attendanceReport", classID, from, to],
    () =>
      axios
        .get("/attendance/report", { params: { classID, from, to } })
        .then((res) => res.data),
    {
      enabled: false,
    }
  );

  const handleGenerate = () => {
    if (classID && from && to) {
      refetch();
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" mb={2}>
        Attendance Report
      </Typography>

      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="class-label">Class</InputLabel>
          <Select
            labelId="class-label"
            value={classID}
            label="Class"
            onChange={(e) => setClassID(e.target.value)}
          >
            {classes &&
              classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!classID || !from || !to}
        >
          Generate
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {report && report.dates.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{ maxWidth: "100%", overflowX: "auto" }}
        >
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#fff",
                    zIndex: 1,
                  }}
                >
                  Student Name
                </TableCell>
                {report.dates.map((date) => (
                  <TableCell
                    key={date}
                    align="center"
                    sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                  >
                    {dayjs(date).format("DD MMM")}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {report.students.map((student) => (
                <TableRow key={student.id}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                    }}
                  >
                    {student.fullName}
                  </TableCell>
                  {report.dates.map((date) => {
                    const status = student.attendance[date];
                    return (
                      <TableCell
                        key={date}
                        align="center"
                        sx={{
                          backgroundColor: status
                            ? statusColors[status]
                            : "#f5f5f5",
                          color: status ? "#fff" : "#999",
                          fontWeight: "bold",
                        }}
                      >
                        {status ? statusLabels[status] : "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {report && report.dates.length === 0 && (
        <Typography color="textSecondary" mt={2}>
          No attendance data found for the selected date range.
        </Typography>
      )}
    </Box>
  );
};

export default AttendanceReport;
